// Phase 62 (Plan 62-06) — To Be Invoiced invoice-builder card for billable projects.
// Surface 1 per 62-UI-SPEC.md.
//
// Renders on ProjectDetailPage when project.billable === true.
// Provides: period navigation, check-to-include Package / Overage / External
// costs blocks (or Billable entries for hourly projects), selective Generate
// invoice action, inline billedAmount edit, and Previous invoices list.

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { Button } from '../common/Button';
import { DismissibleErrorBanner } from '../common/DismissibleErrorBanner';
import { MonthYearPicker } from '../ui/MonthYearPicker';
import { useProjectToBeInvoiced, usePatchBilledAmount } from '../../hooks/useToBeInvoiced';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { ReportingMoney } from '../common/ReportingMoney';
import { BILLING_LOCK_COLORS } from '../common/billingLockColors';
import { summarizeOverageByPersonRole, type OverageSummaryEntry } from '../../utils/overageSummary';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber, numberFormatter } from '../../utils/localeFormat';

// ===== Helpers =====

/** Default billing period: the previous month — the one that just closed. */
function defaultBillingPeriodYM(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
}

function periodToFromTo(ym: string): { from: string; to: string } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(ym);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

/**
 * MKS-549 — `numberOptions` was added for the overage amounts, which need
 * cents where the monthly fee never did. It DEFAULTS to the whole-number options
 * this helper has always hardcoded, so every pre-ticket call site renders
 * byte-identically.
 */
function fmtAmount(
  value: number | string | null | undefined,
  currency: string | null | undefined,
  numberOptions: Intl.NumberFormatOptions = { maximumFractionDigits: 0 }
): string {
  if (value == null) return '';
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return String(value);
  if (!currency) return formatNumber(num, numberOptions, LEGACY_EN_NUMBER_LOCALE);
  try {
    return numberFormatter({
      style: 'currency',
      currency,
      ...numberOptions,
    }, LEGACY_EN_NUMBER_LOCALE).format(num);
  } catch {
    return `${formatNumber(num, numberOptions, LEGACY_EN_NUMBER_LOCALE)} ${currency}`;
  }
}

/**
 * MKS-549 — one overage amount, in the currency it is actually billed in.
 *
 * Overage money is denominated in the project's PACKAGE currency
 * (`Project.balicekCurrency`): the backend bills the hours at the project's own
 * `ProjectOverageRate`, which is quoted in that currency, and converts nothing.
 * `<ReportingMoney>` takes a CZK ledger figure, so handing it 17 380 € rendered
 * "17 380 Kč" — the right number wearing the wrong currency, which is what this
 * ticket is (production 770 "Ahlsell Sweden SLA", EUR, 110 €/h).
 *
 * A CZK package project still goes through `<ReportingMoney>`, deliberately:
 * there the figure really IS the CZK ledger, so MKS-314's reporting-currency
 * conversion and its rate tooltip apply to it exactly as before. So does a
 * payload with no `currency` at all, which is what an older backend sends.
 *
 * The non-CZK amount is NOT converted to CZK for display. The rate that will
 * apply is the CNB fixing of the invoice's own date, and the invoice has not
 * been generated yet — showing the source figure is the honest thing the
 * Package line above already does for the monthly fee.
 */
function OverageMoney({
  value,
  currency,
  period,
}: {
  value: number;
  currency: string | null | undefined;
  period: string;
}) {
  if (!currency || currency === 'CZK') {
    return <ReportingMoney value={value} month={period} />;
  }
  // Same "cents only when there are cents" shape `formatCurrency` and
  // `reportingMoney` use, so the EUR column lines up with the CZK one.
  const hasDecimals = value % 1 !== 0;
  return (
    <>
      {fmtAmount(value, currency, {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      })}
    </>
  );
}

// ===== Overage roll-up by person and role =====

function OverageSummaryTable({
  entries,
  currency,
  period,
}: {
  entries: ReadonlyArray<OverageSummaryEntry>;
  // MKS-549 — the roll-up sums the very same per-entry amounts the table below
  // shows, so it is in the package currency too and renders through
  // `<OverageMoney>`, not `<ReportingMoney>`.
  currency: string | null | undefined;
  period: string;
}) {
  const summary = summarizeOverageByPersonRole(entries);
  return (
    <div className="mb-3" data-testid="overage-summary">
      <div className="mb-1 text-[11px] font-semibold text-ms-text-secondary">
        By person and role
      </div>
      <table className="w-full text-[10.5px] tabular-nums">
        <thead>
          <tr className="text-ms-text-muted border-b border-ms-border">
            <th className="text-left py-1 pr-2 font-semibold">Person</th>
            <th className="text-left py-1 pr-2 font-semibold">Role</th>
            <th className="text-right py-1 pr-2 font-semibold">Hours</th>
            <th className="text-right py-1 pr-2 font-semibold">Rate/h</th>
            <th className="text-right py-1 font-semibold">Billing</th>
          </tr>
        </thead>
        <tbody>
          {summary.rows.map((row) => (
            <tr
              key={`${row.userId}-${row.roleId ?? 'none'}-${row.hourlyRate ?? 'norate'}`}
              className="border-b border-ms-border/40"
            >
              <td className="py-1 pr-2 text-ms-text-secondary">{row.userName}</td>
              <td className="py-1 pr-2 text-ms-text-secondary">{row.roleName ?? '—'}</td>
              <td className="py-1 pr-2 text-right">{row.hours.toFixed(2)}</td>
              <td className="py-1 pr-2 text-right">
                {row.hourlyRate !== null ? (
                  <OverageMoney value={row.hourlyRate} currency={currency} period={period} />
                ) : (
                  '—'
                )}
              </td>
              <td className="py-1 text-right">
                {row.billing !== null ? (
                  <OverageMoney value={row.billing} currency={currency} period={period} />
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold border-t border-ms-border">
            <td className="py-1 pr-2" colSpan={2}>
              Total
            </td>
            <td className="py-1 pr-2 text-right">{summary.totalHours.toFixed(2)}</td>
            <td className="py-1 pr-2" />
            <td className="py-1 text-right">
              <OverageMoney value={summary.totalBilling} currency={currency} period={period} />
            </td>
          </tr>
        </tfoot>
      </table>
      {summary.hasUnratedHours && (
        <p className="mt-1 text-[10.5px] text-ms-text-muted italic">
          Hours without a rate are counted but not billed.
        </p>
      )}
    </div>
  );
}

// ===== Status badge colors (inline style only, per UI-SPEC Color section) =====

const STATUS_COLORS = {
  needs_approval: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  ready: { bg: '#D1FAE5', fg: '#065F46', dot: '#10B981' },
} as const;

// ===== Inclusion state =====

interface InclusionState {
  package: boolean;
  overage: boolean;
  expenseReceiptIds: number[];
  entryIds: number[];
  /**
   * MKS-280 — include the leftover of an earlier, already-invoiced month.
   *
   * A flag rather than an id list, like Package and Overage: the block is
   * all-or-nothing here (per-person ticking is the review page's job), and the
   * ids are read from the payload at generate time, so switching the period
   * can never carry last month's leftover ids into this month's invoice.
   */
  carried: boolean;
}

// ===== Props =====

export interface ToBeInvoicedCardProps {
  projectId: number;
  /**
   * Review fix WR-06: billedAmount edits and invoice generation are gated to
   * admin/finance on the backend — pass false for PM viewers so the inputs
   * and Generate button do not offer actions that would 403.
   */
  canEdit?: boolean;
}

// ===== Component =====

export function ToBeInvoicedCard({ projectId, canEdit = true }: ToBeInvoicedCardProps) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<string>(defaultBillingPeriodYM);
  const [overageDetailOpen, setOverageDetailOpen] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Inclusion state — which billing components to include in the generated invoice
  const [inclusion, setInclusion] = useState<InclusionState>({
    package: true,
    overage: false,
    expenseReceiptIds: [],
    entryIds: [],
    carried: false,
  });

  // Per-receipt billedAmount local state for optimistic edits
  const [receiptAmounts, setReceiptAmounts] = useState<Record<number, string>>({});
  const [receiptErrors, setReceiptErrors] = useState<Record<number, string>>({});

  // Review fix WR-07: editing a billedAmount input and clicking "Generate
  // invoice" in one motion fires onBlur (async PATCH) and onClick (POST)
  // without ordering — the backend reads billedAmount from the DB at generate
  // time, so the invoice could ledger the stale pre-edit value. Track
  // in-flight saves so handleGenerate can await them before generating.
  const pendingSavesRef = useRef<Set<Promise<unknown>>>(new Set());

  // MKS-205 — the invoice history is capped at the newest 5; "Show more"
  // re-requests it uncapped.
  const [showAllInvoices, setShowAllInvoices] = useState(false);

  const { data, isLoading, error } = useProjectToBeInvoiced(
    projectId,
    period,
    true,
    showAllInvoices ? 'all' : 'recent',
  );
  const patchBilledAmount = usePatchBilledAmount();

  // ===== Loading state =====
  if (isLoading) {
    return (
      <Card title="To Be Invoiced">
        <div className="h-24 animate-pulse bg-ms-surface-2 rounded" />
      </Card>
    );
  }

  const tbi = data?.data;

  // ===== Error state =====
  if (error || !tbi) {
    return (
      <Card title="To Be Invoiced">
        <p className="text-[12px] text-red-600">
          Failed to load billing data. Refresh the page to try again.
        </p>
      </Card>
    );
  }

  const isBalicek = !!tbi.package;
  const pkg = tbi.package;
  const ovg = tbi.overage;
  const hourly = tbi.hourlyEntries;
  // MKS-280 — hours an earlier month's invoice left behind (unticked then, or
  // approved after it went out). Present only while the release flag is on and
  // only when the project actually has some, so a flag-off card is unchanged.
  const carried = tbi.carriedOver;
  const carriedEntryIds = carried?.entryIds ?? [];
  const expenses = tbi.expenses ?? [];
  const prevInvoices = tbi.previousInvoices ?? [];
  // MKS-205 — how many invoices the capped list is hiding, and the hours the
  // amounts above leave out because approval has not cleared them yet.
  const olderInvoiceCount = Math.max(
    0,
    (tbi.previousInvoicesTotal ?? prevInvoices.length) - prevInvoices.length,
  );
  const hasOlderInvoices = olderInvoiceCount > 0;
  const unapprovedHours = tbi.unapprovedHours ?? 0;

  // Receipts use local overrides for optimistic editing
  const getReceiptAmount = (receipt: { id: number; billedAmount: number | null; amount: number | null }) => {
    if (receiptAmounts[receipt.id] !== undefined) return receiptAmounts[receipt.id];
    return String(receipt.billedAmount ?? receipt.amount ?? '');
  };

  // ===== Enablement logic for Generate button =====
  const hasPackageChecked = inclusion.package && !!pkg;
  const hasOverageChecked = inclusion.overage && !!ovg;
  const hasReceiptsChecked = inclusion.expenseReceiptIds.length > 0;
  const hasEntriesChecked = inclusion.entryIds.length > 0;
  // MKS-280 — the leftover is a billing component of its own. Without this a
  // month whose ONLY outstanding work is a carried-over leftover offered
  // nothing to tick and left "Review & generate invoice" disabled, which is
  // the bug staging verification reported.
  const hasCarriedChecked = inclusion.carried && carriedEntryIds.length > 0;

  const overageBlocked =
    hasOverageChecked && ovg?.overageStatus === 'needs_approval';

  const anythingChecked =
    hasPackageChecked ||
    hasOverageChecked ||
    hasReceiptsChecked ||
    hasEntriesChecked ||
    hasCarriedChecked;

  const generateEnabled = anythingChecked && !overageBlocked;

  // ===== Generate handler =====
  // Quick task 260912-j8z (G-02, D-Gap2 FINAL) — this card CONFIGURES an
  // invoice; it no longer creates one. All the guards below still gate
  // whether the button is allowed to proceed, but the outcome is a
  // navigation to `/invoices/new`, which CONFIRMS and issues the invoice
  // after the operator has seen the full breakdown and can review or
  // override the discount.
  const handleGenerate = async () => {
    setGenerateError(null);

    if (!anythingChecked) {
      setGenerateError('Select at least one billing component before generating.');
      return;
    }
    if (overageBlocked) {
      setGenerateError(
        'Overage cannot be billed until approved. Approve overage hours first or uncheck the Overage block.',
      );
      return;
    }

    // Duplicate period guard. MKS-205: prefer the server's answer — the
    // Previous invoices list below is capped, so it can no longer be trusted to
    // contain the period's invoice. Older payloads (no field) keep the
    // client-side check.
    const alreadyExists =
      tbi.hasInvoiceForPeriod ??
      prevInvoices.some((inv) => inv.from && inv.from.slice(0, 7) === period);
    if (alreadyExists) {
      setGenerateError(
        `An invoice for ${period} already exists. Delete the existing invoice before regenerating.`,
      );
      return;
    }

    const range = periodToFromTo(period);
    if (!range) {
      setGenerateError('Invalid period format.');
      return;
    }

    // Review fix WR-07: wait for any in-flight billedAmount saves so the
    // generate page reads the just-edited value when IT reads from the
    // database. This matters MORE now: the card no longer generates, so the
    // generate page's own preview/generate calls are the only reads left.
    if (pendingSavesRef.current.size > 0) {
      await Promise.allSettled([...pendingSavesRef.current]);
    }

    // Encode the block selection into the query string alongside the three
    // params InvoiceGeneratePage already parses (projectIds, from, to).
    // `sel=1` marks "a selection WAS carried" so the receiving page can tell
    // an intentional empty list (bill none of that block) apart from an
    // absent one (bill everything — today's un-carried behaviour).
    // Review fix CR-01's rule survives the hop unchanged: expenseReceiptIds
    // is always sent explicitly (possibly empty); entryIds is OMITTED for
    // Hour Package projects (isBalicek) so the generate page decodes it as
    // `undefined` — the period's entries stay linked for audit, exactly as
    // this card sent it directly before this task.
    // MKS-280 — the leftover travels in the SAME `entryIds=` list. The review
    // page and generation both accept entries dated outside [from, to] when a
    // tick list is carried: `invoiceService` widens its window by the closed
    // months the leftover sits in (`resolveCarryoverPeriods`). The ids are
    // read from the payload now rather than from state, so they always belong
    // to the period on screen.
    const receiptIdsParam = inclusion.expenseReceiptIds.join(',');
    const selectedEntryIds = [
      ...inclusion.entryIds,
      ...(hasCarriedChecked ? carriedEntryIds : []),
    ];
    const entryIdsSegment = isBalicek ? '' : `&entryIds=${selectedEntryIds.join(',')}`;
    const url =
      `/invoices/new?projectIds=${projectId}&from=${range.from}&to=${range.to}` +
      `&sel=1&pkg=${hasPackageChecked ? '1' : '0'}&ovg=${hasOverageChecked ? '1' : '0'}` +
      `&receiptIds=${receiptIdsParam}${entryIdsSegment}`;

    navigate(url);
  };

  // ===== billedAmount blur handler =====
  const handleBilledAmountBlur = async (receiptId: number, newValueStr: string, prevValueStr: string) => {
    const newValue = parseFloat(newValueStr);
    if (!Number.isFinite(newValue) || newValue < 0) {
      setReceiptErrors((prev) => ({ ...prev, [receiptId]: 'Enter a valid amount (0 or greater).' }));
      // Revert to previous value
      setReceiptAmounts((prev) => ({ ...prev, [receiptId]: prevValueStr }));
      return;
    }
    setReceiptErrors((prev) => {
      const next = { ...prev };
      delete next[receiptId];
      return next;
    });
    // Review fix WR-07: register the in-flight save so a Generate click in
    // the same motion can await it (see pendingSavesRef above).
    const save = patchBilledAmount.mutateAsync({ id: receiptId, billedAmount: newValue });
    pendingSavesRef.current.add(save);
    try {
      await save;
    } catch {
      setReceiptErrors((prev) => ({
        ...prev,
        [receiptId]: 'Failed to save billed amount.',
      }));
      setReceiptAmounts((prev) => ({ ...prev, [receiptId]: prevValueStr }));
    } finally {
      pendingSavesRef.current.delete(save);
    }
  };

  // ===== External costs block-level checkbox =====
  const allReceiptsChecked = expenses.length > 0 && expenses.every((r) => inclusion.expenseReceiptIds.includes(r.id));

  const handleExternalCostsBlockCheck = (checked: boolean) => {
    setInclusion((prev) => ({
      ...prev,
      expenseReceiptIds: checked ? expenses.map((r) => r.id) : [],
    }));
  };

  const handleReceiptCheck = (receiptId: number, checked: boolean) => {
    setInclusion((prev) => ({
      ...prev,
      expenseReceiptIds: checked
        ? [...prev.expenseReceiptIds, receiptId]
        : prev.expenseReceiptIds.filter((id) => id !== receiptId),
    }));
  };

  return (
    <Card title="To Be Invoiced">
      {/* Generate error banner */}
      <DismissibleErrorBanner message={generateError} onDismiss={() => setGenerateError(null)} />

      {/* Period selector — shared MonthYearPicker (English-only, YYYY-MM contract) */}
      <div className="flex items-center gap-3 mb-4">
        <MonthYearPicker value={period} onChange={setPeriod} label="Period" />
      </div>

      <div className="space-y-4">
        {/* ===== Unapproved hours warning (MKS-205) =====
            Only approved entries are billable, so hours still waiting for
            approval are missing from every amount below. */}
        {unapprovedHours > 0 && (
          <div
            className="rounded border px-3 py-2 text-[12px]"
            style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B', color: '#92400E' }}
          >
            {unapprovedHours} h awaiting approval in this period — not included in the
            amounts below. Approve the timesheets first to bill them.
          </div>
        )}

        {/* ===== Package block (Hour Package projects) ===== */}
        {isBalicek && pkg && (
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id={`tbi-package-${projectId}`}
              checked={inclusion.package}
              onChange={(e) => setInclusion((prev) => ({ ...prev, package: e.target.checked }))}
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div className="flex-1">
              <label
                htmlFor={`tbi-package-${projectId}`}
                className="text-sm font-semibold text-ms-text-strong cursor-pointer"
              >
                Package
              </label>
              <span className="text-[12px] tabular-nums text-ms-text-secondary ml-2">
                {pkg.balicekHours} h / {fmtAmount(pkg.paushalAmount, pkg.paushalCurrency)}
                {pkg.paushalCurrency !== 'CZK' && pkg.packageCzk != null && pkg.packageCzk > 0
                  ? ` ≈ ${formatCurrency(pkg.packageCzk)}`
                  : ''}
              </span>
            </div>
          </div>
        )}

        {/* ===== Overage block (Hour Package projects with over-limit allowed) ===== */}
        {isBalicek && ovg && (
          // MKS-549 — testid so a currency assertion can be scoped to this
          // block: the Previous invoices list below is CZK and always will be.
          <div data-testid="tbi-overage">
            <div
              className={`flex items-start gap-2 ${
                ovg.overageStatus === 'needs_approval' ? 'opacity-50' : ''
              }`}
            >
              <input
                type="checkbox"
                id={`tbi-overage-${projectId}`}
                checked={inclusion.overage}
                disabled={ovg.overageStatus === 'needs_approval'}
                onChange={(e) => setInclusion((prev) => ({ ...prev, overage: e.target.checked }))}
                className={`mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                  ovg.overageStatus === 'needs_approval' ? 'cursor-not-allowed' : ''
                }`}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <label
                    htmlFor={`tbi-overage-${projectId}`}
                    className={`text-sm font-semibold text-ms-text-strong ${
                      ovg.overageStatus === 'needs_approval' ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    Overage
                  </label>
                  {ovg.overageStatus === 'needs_approval' ? (
                    <>
                      <Pill
                        bg={STATUS_COLORS.needs_approval.bg}
                        fg={STATUS_COLORS.needs_approval.fg}
                        dot={STATUS_COLORS.needs_approval.dot}
                      >
                        Needs approval
                      </Pill>
                      <a
                        href="#overage-approvals"
                        className="text-[12px] text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Approve overage →
                      </a>
                    </>
                  ) : (
                    <span className="text-[12px] tabular-nums text-ms-text-secondary">
                      {ovg.totalHours.toFixed(2)} h ·{' '}
                      <OverageMoney
                        value={ovg.totalBilling}
                        currency={ovg.currency}
                        period={period}
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Overage detail expander */}
            <div className="ml-6 mt-1">
              <button
                type="button"
                onClick={() => setOverageDetailOpen((o) => !o)}
                className="flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-800"
              >
                {overageDetailOpen ? (
                  <ChevronUpIcon className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDownIcon className="h-3.5 w-3.5" />
                )}
                {overageDetailOpen ? 'Hide detail' : 'Detail'}
              </button>

              {overageDetailOpen && (
                <div className="mt-2">
                  {!ovg.entries || ovg.entries.length === 0 ? (
                    <p className="text-[12px] text-ms-text-secondary italic">
                      No overage hours this period.
                    </p>
                  ) : (
                    <>
                    <OverageSummaryTable
                      entries={ovg.entries}
                      currency={ovg.currency}
                      period={period}
                    />
                    <table className="w-full text-[10.5px] tabular-nums">
                      <thead>
                        <tr className="text-ms-text-muted border-b border-ms-border">
                          <th className="text-left py-1 pr-2 font-semibold">User</th>
                          <th className="text-left py-1 pr-2 font-semibold">Date</th>
                          <th className="text-right py-1 pr-2 font-semibold">Hours</th>
                          <th className="text-left py-1 pr-2 font-semibold">Role</th>
                          <th className="text-right py-1 pr-2 font-semibold">Rate/h</th>
                          <th className="text-right py-1 font-semibold">Billing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ovg.entries.map((entry) => (
                          <tr key={entry.entryId} className="border-b border-ms-border/40">
                            <td className="py-1 pr-2 text-ms-text-secondary">{entry.userName}</td>
                            <td className="py-1 pr-2 text-ms-text-secondary">{entry.date}</td>
                            <td className="py-1 pr-2 text-right">{entry.overageSlice.toFixed(2)}</td>
                            <td className="py-1 pr-2 text-ms-text-secondary">
                              {entry.roleName ?? '—'}
                            </td>
                            <td className="py-1 pr-2 text-right">
                              {entry.hasRate ? (
                                <OverageMoney
                                  value={entry.hourlyRate}
                                  currency={ovg.currency}
                                  period={period}
                                />
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="py-1 text-right">
                              {entry.hasRate ? (
                                <OverageMoney
                                  value={entry.billing}
                                  currency={ovg.currency}
                                  period={period}
                                />
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Hourly entries block (non-Hour-Package projects) ===== */}
        {!isBalicek && hourly && (
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id={`tbi-entries-${projectId}`}
              checked={inclusion.entryIds.length > 0}
              onChange={(e) =>
                setInclusion((prev) => ({
                  ...prev,
                  entryIds: e.target.checked ? (hourly.entryIds ?? []) : [],
                }))
              }
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <label
                  htmlFor={`tbi-entries-${projectId}`}
                  className="text-sm font-semibold text-ms-text-strong cursor-pointer"
                >
                  Billable entries
                </label>
                {hourly.provisional ? (
                  <Pill
                    bg={BILLING_LOCK_COLORS.provisional.bg}
                    fg={BILLING_LOCK_COLORS.provisional.fg}
                    dot={BILLING_LOCK_COLORS.provisional.dot}
                  >
                    Provisional (open period)
                  </Pill>
                ) : (
                  <Pill
                    bg={BILLING_LOCK_COLORS.locked.bg}
                    fg={BILLING_LOCK_COLORS.locked.fg}
                    dot={BILLING_LOCK_COLORS.locked.dot}
                  >
                    Locked
                  </Pill>
                )}
              </div>
              <span className="text-[12px] tabular-nums text-ms-text-secondary ml-2">
                {hourly.totalHours.toFixed(2)} h ·{' '}
                <ReportingMoney value={hourly.totalBilling} month={period} />
              </span>
            </div>
          </div>
        )}

        {/* ===== MKS-280 — left over from a month that is already invoiced =====

            Its own group, exactly as on the review page: these hours belong to
            an earlier month whose invoice has gone out, and they are what the
            copy has always promised would "come back in next period's queue".
            Before this the card knew nothing about them, so in a month whose
            only outstanding work was a leftover it showed no item at all and
            the button below stayed disabled. */}
        {!isBalicek && carried && (
          <div data-testid="tbi-carried-over">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id={`tbi-carried-${projectId}`}
                checked={inclusion.carried}
                disabled={carriedEntryIds.length === 0}
                onChange={(e) => setInclusion((prev) => ({ ...prev, carried: e.target.checked }))}
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1">
                <label
                  htmlFor={`tbi-carried-${projectId}`}
                  className="text-sm font-semibold text-ms-text-strong cursor-pointer"
                >
                  Carried over from {carried.periods.join(', ')}
                </label>
                <span className="text-[12px] tabular-nums text-ms-text-secondary ml-2">
                  {/* MKS-314 follow-up — deliberately NOT re-denominated. These
                      hours are dated in SEVERAL earlier months (`carried.periods`)
                      and the payload carries one lump sum, not a per-month split,
                      so there is no way to apply each month's own rate. Converting
                      the lump at one rate would contradict the rule the rest of
                      this card follows. */}
                  {carried.totalHours.toFixed(2)} h · {formatCurrency(carried.totalBilling)}
                </span>
              </div>
            </div>
            <p className="ml-6 mt-1 text-[11px] text-ms-text-secondary">
              Still unbilled after that month’s invoice — left unticked then, or approved after it
              went out. Ticked here, they go on this invoice.
            </p>
          </div>
        )}

        {/* ===== External costs block ===== */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              id={`tbi-ext-block-${projectId}`}
              checked={allReceiptsChecked}
              onChange={(e) => handleExternalCostsBlockCheck(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ms-text-muted">
              External costs
            </p>
          </div>

          {expenses.length === 0 ? (
            <p className="text-[12px] text-ms-text-secondary italic ml-6">
              No expense receipts for this period.
            </p>
          ) : (
            <div className="space-y-1 ml-6">
              {expenses.map((receipt) => {
                const prevAmount = String(receipt.billedAmount ?? receipt.amount ?? '');
                const currentAmount = getReceiptAmount(receipt);
                return (
                  <div key={receipt.id}>
                    <div className="flex items-center gap-3 py-2">
                      <span className="text-[12px] text-ms-text-muted">
                        {formatDisplayDate(receipt.receiptDate)}
                      </span>
                      <span className="text-[12px] text-ms-text-strong truncate max-w-[200px]">
                        {receipt.description ?? '—'}
                      </span>
                      <span className="text-[12px] tabular-nums text-ms-text-secondary">
                        paid {fmtAmount(receipt.amount, null)}
                      </span>
                      <span className="text-[12px] text-ms-text-secondary">billed</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentAmount}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setReceiptAmounts((prev) => ({ ...prev, [receipt.id]: e.target.value }))
                        }
                        onBlur={(e) =>
                          handleBilledAmountBlur(receipt.id, e.target.value, prevAmount)
                        }
                        className="w-24 rounded border border-gray-300 px-2 py-1 text-[12px] tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                      <input
                        type="checkbox"
                        checked={inclusion.expenseReceiptIds.includes(receipt.id)}
                        onChange={(e) => handleReceiptCheck(receipt.id, e.target.checked)}
                        className="ml-auto rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </div>
                    {receiptErrors[receipt.id] && (
                      <p className="text-[10.5px] text-red-600 ml-0">
                        {receiptErrors[receipt.id]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ===== Review & generate invoice button =====
          Quick task 260912-j8z (G-02) — this button no longer creates an
          invoice. It opens the generate page with this selection carried,
          where the whole document (including the discount) can be reviewed
          or overridden before the invoice is issued. */}
      <div className="flex flex-col items-end gap-1 mt-4">
        <p className="text-[11px] text-ms-text-secondary text-right max-w-xs">
          Opens the invoice preview, where the discount can be reviewed and overridden
          before the invoice is issued.
        </p>
        <Button
          variant="primary"
          disabled={!generateEnabled || !canEdit || patchBilledAmount.isPending}
          onClick={handleGenerate}
        >
          Review &amp; generate invoice
        </Button>
      </div>

      {/* ===== Previous invoices ===== */}
      <div className="border-t border-ms-border mt-6 pt-4">
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ms-text-muted mb-2">
          Previous invoices
        </p>
        {prevInvoices.length === 0 ? (
          <p className="text-[12px] text-ms-text-secondary italic">
            No invoices issued for this project yet.
          </p>
        ) : (
          <div>
            {[...prevInvoices]
              .sort((a, b) => {
                const aP = a.from?.slice(0, 7) ?? '';
                const bP = b.from?.slice(0, 7) ?? '';
                return bP.localeCompare(aP);
              })
              .map((inv) => (
                <div key={inv.id} className="flex items-center justify-between py-2 text-[12px]">
                  <span className="font-mono font-semibold tabular-nums">
                    {inv.from?.slice(0, 7) ?? '—'}
                  </span>
                  {/* MKS-314 follow-up — an ISSUED invoice keeps its stored
                      amount and the rate it was generated with. Never
                      re-converted; that is the product decision, not an
                      omission. */}
                  <span className="tabular-nums">
                    {formatCurrency(inv.invoicedAmount)}
                  </span>
                  <Link
                    to={`/invoices/${inv.id}`}
                    className="text-blue-600 hover:underline text-[12px]"
                  >
                    Detail →
                  </Link>
                </div>
              ))}

            {/* MKS-205 — the list above is the newest 5; load the rest on demand. */}
            {hasOlderInvoices && (
              <button
                type="button"
                onClick={() => setShowAllInvoices(true)}
                className="mt-1 text-[12px] text-blue-600 hover:underline"
              >
                Show more ({olderInvoiceCount} older)
              </button>
            )}
            {showAllInvoices && !hasOlderInvoices && (
              <button
                type="button"
                onClick={() => setShowAllInvoices(false)}
                className="mt-1 text-[12px] text-blue-600 hover:underline"
              >
                Show less
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
